import type { TourDefinition, TourStep } from "@/config/tours";

/**
 * Clinical training tours. Most start on the trainee's practice patient chart
 * (`/patient/<id>`, Overview tab). Steps that need another part of the chart
 * switch to it with a query-only `nextRoute` ("?tab=..."), which keeps the
 * patient's path; the step after it carries a `prevRoute` back to Overview so
 * Back reloads the tab and Onborda can find the earlier step again.
 *
 * The tour covers the screen, so dialogs and drawers are never open while it
 * runs: steps point at the button to click and say what comes next.
 */

const tabSelector = (tab: string) =>
  `#tour-train-patient-tabs [data-tab-id='${tab}']`;

const step = (
  title: string,
  content: string,
  selector: string,
  side: TourStep["side"],
  extra: Partial<TourStep> = {},
): TourStep => ({
  icon: null,
  title,
  content,
  selector,
  side,
  pointerPadding: 8,
  pointerRadius: 8,
  ...extra,
});

const BACK_TO_OVERVIEW = { prevRoute: "?tab=overview" };

const NEW_PATIENT = "#tour-train-new-patient";
const CREATE_NOTE = "#tour-train-create-note";
const FORMS_HEADER = "#forms-documents-section > div:first-child";
const SEND_FORMS = "#tour-train-send-forms";
const ADD_DOSE = "#tour-train-add-dose";
const E_PRESCRIBE = "#tour-train-e-prescribe";
const GFE_REQUEST = "#tour-train-gfe-request, #tour-train-qualiphy-gfe-request";
const GFE_HEADER =
  "#tour-train-gfe-section > div:first-child, #tour-train-qualiphy-gfe-section > div:first-child";

export const clinicalTrainingTours: TourDefinition[] = [
  {
    tour: "training-add-patient",
    steps: [
      step(
        "Add a new patient",
        "Click New to open the Create a patient window.",
        NEW_PATIENT,
        "left",
      ),
      step(
        "Enter their details",
        "Type the first and last name and the date of birth. Add a phone number or email so they get reminders and forms.",
        NEW_PATIENT,
        "left",
      ),
      step(
        "Save the patient",
        "Click Create Patient. Decoda opens the new patient's chart straight away.",
        NEW_PATIENT,
        "left",
      ),
      step(
        "Find them again",
        "Every patient is listed here. Search by name, phone number or date of birth.",
        "#tour-train-patient-list",
        "top",
      ),
    ],
  },
  {
    tour: "training-patient-chart",
    steps: [
      step(
        "Patient details",
        "The patient's name, date of birth and contact details are always at the top. Click a phone number or email to reach them.",
        "#tour-train-patient-header",
        "right",
      ),
      step(
        "Move around the chart",
        "Each item opens a part of the chart: appointments, notes, billing, forms, photos and more.",
        "#tour-train-patient-tabs",
        "right",
      ),
      step(
        "Notes and treatments",
        "Medical Notes & Data holds visit notes, measurements and the doses given at each treatment.",
        tabSelector("medical-notes"),
        "right",
      ),
      step(
        "Forms",
        "Forms & Documents shows the intake and consent forms the patient filled in, plus any uploaded files.",
        tabSelector("forms-documents"),
        "right",
      ),
      step(
        "Photos",
        "Gallery keeps the patient's before and after photos, grouped by date.",
        tabSelector("gallery"),
        "right",
      ),
      step(
        "Start a note",
        "Click Create Medical Note to start a visit note, with AI Scribe or a template.",
        CREATE_NOTE,
        "right",
      ),
    ],
  },
  {
    tour: "training-review-forms",
    steps: [
      step(
        "Open the patient's forms",
        "Forms & Documents holds every form the patient has filled in. Click Next to open it.",
        tabSelector("forms-documents"),
        "right",
        { nextRoute: "?tab=forms-documents" },
      ),
      step(
        "Find the form",
        "Each row is a form the patient sent back, with the date. Partial Submission means they did not finish it.",
        FORMS_HEADER,
        "bottom",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Read the answers",
        "Click the ⋯ button on a form's row and choose View submission to read every answer.",
        FORMS_HEADER,
        "bottom",
      ),
      step(
        "Check for allergies and flags",
        "Look closely at allergies, medications and any health questions answered yes. Tell the provider about anything that stands out before treatment.",
        FORMS_HEADER,
        "bottom",
      ),
    ],
  },
  {
    tour: "training-fill-form",
    steps: [
      step(
        "Open the patient's forms",
        "Forms are filled in from Forms & Documents. Click Next to open it.",
        tabSelector("forms-documents"),
        "right",
        { nextRoute: "?tab=forms-documents" },
      ),
      step(
        "Open the send window",
        "Click Send to Patient. The same window lets the patient fill a form in person.",
        SEND_FORMS,
        "left",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Pick the form",
        "Choose an intake or consent form, then pick In Person at the bottom of the window.",
        SEND_FORMS,
        "left",
      ),
      step(
        "Hand over the device",
        "Click Fill In Person. The form opens in a new tab so the patient can fill it in and sign on this device.",
        SEND_FORMS,
        "left",
      ),
      step(
        "See the answers",
        "Once the patient submits the form, it shows up in this list.",
        FORMS_HEADER,
        "bottom",
      ),
    ],
  },
  {
    tour: "training-ai-scribe",
    steps: [
      step(
        "Start a new note",
        "Click Create Medical Note and choose AI Note.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Start recording",
        "A small recording bar appears near the top of the screen. Click Start Recording and allow the microphone if your browser asks.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Talk through the visit",
        "Speak as you would with a patient. Use the pause button on the bar if you need a break.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Stop and draft",
        "Click Generate Note when you are done. Decoda stops recording and writes a draft note.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Check the draft",
        "Read every section and fix anything before you save. You can find the note any time under Medical Notes & Data.",
        tabSelector("medical-notes"),
        "right",
      ),
    ],
  },
  {
    tour: "training-note-template",
    steps: [
      step(
        "Start a note",
        "Click Create Medical Note and choose Manual Note.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Choose a template",
        "In the window, pick a template such as a neurotoxin or filler note, then click Create Note.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Fill in the fields",
        "The note opens on its own screen. Fill in what you can; some fields are filled in from the chart for you.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Save it",
        "Click Save at the top of the note. A saved note can be finished later, by you or the provider.",
        CREATE_NOTE,
        "right",
      ),
      step(
        "Find the note",
        "Saved notes are listed under Medical Notes & Data on the chart.",
        tabSelector("medical-notes"),
        "right",
      ),
    ],
  },
  {
    tour: "training-photos",
    steps: [
      step(
        "Open the gallery",
        "Gallery keeps all of the patient's photos. Click Next to open it.",
        tabSelector("gallery"),
        "right",
        { nextRoute: "?tab=gallery" },
      ),
      step(
        "Take a photo",
        "Click Capture to take a photo with this device's camera.",
        "#tour-train-gallery [aria-label='Capture']",
        "left",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Upload a file",
        "Or click this button and choose Add files to upload a picture from your computer. Photos show here, grouped by date.",
        "#tour-train-gallery [aria-label='Attachment actions']",
        "left",
      ),
    ],
  },
  {
    tour: "training-log-dose",
    steps: [
      step(
        "Open notes and data",
        "Doses are logged under Medical Notes & Data. Click Next to open it.",
        tabSelector("medical-notes"),
        "right",
        { nextRoute: "?tab=medical-notes" },
      ),
      step(
        "Add a dose",
        "Click Add Dose in the Medication Doses section.",
        ADD_DOSE,
        "left",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Pick the product",
        "Choose the product you used, then enter the amount and unit.",
        ADD_DOSE,
        "left",
      ),
      step(
        "Add the area",
        "Under Administration Location, add each treatment area and how much went there.",
        ADD_DOSE,
        "left",
      ),
      step(
        "Save the dose",
        "Click Save Dose. It keeps your stock counts and the patient's history up to date.",
        ADD_DOSE,
        "left",
      ),
    ],
  },
  {
    tour: "training-e-prescribe",
    steps: [
      step(
        "Open notes and data",
        "E-prescribing starts from Medical Notes & Data. Click Next to open it.",
        tabSelector("medical-notes"),
        "right",
        { nextRoute: "?tab=medical-notes" },
      ),
      step(
        "Open e-prescribing",
        "Click E-prescribe. DoseSpot opens in a new tab. The first time, it asks you to accept its terms.",
        E_PRESCRIBE,
        "left",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Add a pharmacy",
        "In DoseSpot, add a pharmacy for the patient if they do not have one yet.",
        E_PRESCRIBE,
        "left",
      ),
      step(
        "Send the prescription",
        "Search for a non-controlled medication, fill in the directions and send it. This is DoseSpot's test system, so nothing reaches a real pharmacy.",
        E_PRESCRIBE,
        "left",
      ),
    ],
  },
  {
    tour: "training-gfe",
    steps: [
      step(
        "Open appointments",
        "Good Faith Exams are kept with the patient's appointments. Click Next to open them.",
        tabSelector("appointments"),
        "right",
        { nextRoute: "?tab=appointments" },
      ),
      step(
        "Request an exam",
        "Click Request GFE to send the patient for a Good Faith Exam.",
        GFE_REQUEST,
        "left",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Fill in the request",
        "Pick the treatments or exams the patient needs, check the details and send it. In the sandbox it goes to the exam provider's test system.",
        GFE_REQUEST,
        "left",
      ),
      step(
        "Track the result",
        "The exam shows here with its status, so you can see when the patient is cleared for treatment.",
        GFE_HEADER,
        "bottom",
      ),
    ],
  },
  {
    tour: "training-sign-note",
    steps: [
      step(
        "Open notes and data",
        "Your notes are under Medical Notes & Data. Click Next to open it.",
        tabSelector("medical-notes"),
        "right",
        { nextRoute: "?tab=medical-notes" },
      ),
      step(
        "Find your note",
        "Notes are listed here, newest first. Click a note to open it and check it over.",
        "#notes-section > div:first-child",
        "bottom",
        BACK_TO_OVERVIEW,
      ),
      step(
        "Sign it off",
        "Click the ⋯ button on the note's row and choose Sign Off. An open note also has a Sign off button at the top.",
        "#notes-section > div:first-child",
        "bottom",
      ),
      step(
        "After signing",
        "A signed note is locked. Any later changes are added as an addendum.",
        "#notes-section > div:first-child",
        "bottom",
      ),
    ],
  },
];
